import React from 'react';
import { Check, AlertTriangle } from 'lucide-react';
import { useTheme } from '../../lib/theme';

const PLAN_LABEL = { starter: 'Starter', business: 'Business', accountant: 'Accountant' };

export default function CurrentPlanCard({ subscription, onRetryPayment, onChangePlan }) {
  const { colors } = useTheme();
  const isAdmin = subscription?.is_admin_override || subscription?.plan_code === 'admin';
  const trial = subscription?.is_trialing;
  const pastDue = subscription?.status === 'past_due';
  const planName = PLAN_LABEL[subscription?.plan_code] || 'No plan';
  const nextBilling = subscription?.current_period_end ? new Date(subscription.current_period_end) : null;
  const trialEnd = subscription?.trial_ends_at ? new Date(subscription.trial_ends_at) : null;

  if (isAdmin) {
    return (
      <div
        data-testid="current-plan-card-admin"
        style={{
          padding: 24, borderRadius: 16,
          background: colors.surface,
          border: `1px solid ${colors.accent}`,
          boxShadow: `0 0 0 4px ${colors.accent}22, 0 20px 50px -30px ${colors.accent}`,
          position: 'relative', overflow: 'hidden',
        }}
      >
        {/* Subtle green mesh glow */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: `radial-gradient(400px 180px at 100% 0%, ${colors.accentSoft}, transparent 70%)`,
        }} />
        <div style={{ position: 'relative' }}>
          <div style={{ color: colors.textMuted, fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Current plan
          </div>
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: colors.text, letterSpacing: '-0.02em' }}>
              Admin Account
            </div>
            <span style={{
              padding: '3px 12px', borderRadius: 999,
              background: colors.accentSoft, color: colors.accent,
              fontSize: 11, fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase',
            }}>
              Unlimited access
            </span>
          </div>
          <div style={{ marginTop: 10, color: colors.textMuted, fontSize: 14, lineHeight: 1.55, maxWidth: 540 }}>
            Your admin account has unrestricted access to all features. No billing applies.
          </div>
        </div>
      </div>
    );
  }

  const ringColor = trial ? colors.accent : (pastDue ? '#F59E0B' : colors.border);

  return (
    <div
      data-testid="current-plan-card"
      style={{
        padding: 24, borderRadius: 16,
        background: colors.surface, border: `1px solid ${colors.border}`,
        boxShadow: trial ? `0 0 0 4px ${ringColor}22` : 'none',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ color: colors.textMuted, fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Current plan</div>
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: colors.text, letterSpacing: '-0.02em' }}>{planName}</div>
            {subscription?.status && (
              <StatusBadge status={subscription.status} colors={colors} />
            )}
          </div>
          {trial && trialEnd && (
            <div style={{ marginTop: 8, color: colors.accent, fontSize: 13, fontWeight: 600 }}>
              Free trial — ends {trialEnd.toLocaleDateString()}
            </div>
          )}
          {!trial && nextBilling && (
            <div style={{ marginTop: 8, color: colors.textMuted, fontSize: 13 }}>
              Next billing: {nextBilling.toLocaleDateString()}
            </div>
          )}
          {subscription?.cancel_at_period_end && (
            <div style={{ marginTop: 8, color: '#F59E0B', fontSize: 13, fontWeight: 600 }}>
              Will cancel at period end
            </div>
          )}
        </div>
        {subscription?.plan_code && (
          <button
            type="button" onClick={onChangePlan}
            data-testid="change-plan-btn"
            style={{
              padding: '10px 16px', borderRadius: 10,
              background: colors.accent, color: 'white', border: 'none',
              fontWeight: 700, fontSize: 13, cursor: 'pointer',
              boxShadow: `0 10px 24px -14px ${colors.accent}`,
            }}
          >
            Change plan
          </button>
        )}
      </div>

      {pastDue && (
        <div
          data-testid="past-due-alert"
          style={{
            marginTop: 14, padding: 12, borderRadius: 10,
            background: '#FFFBEB', border: '1px solid #FDE68A', color: '#92400E',
            display: 'flex', alignItems: 'center', gap: 10, fontSize: 13,
          }}
        >
          <AlertTriangle size={16} />
          <span style={{ flex: 1 }}>Your last payment failed. Please retry to keep your account active.</span>
          <button
            type="button" onClick={onRetryPayment}
            style={{
              padding: '8px 14px', borderRadius: 8, border: 'none',
              background: '#92400E', color: 'white', fontWeight: 700, fontSize: 12, cursor: 'pointer',
            }}
          >Retry payment</button>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status, colors }) {
  const map = {
    trialing: { bg: colors.accentSoft, fg: colors.accent, label: 'Trial' },
    active: { bg: colors.accentSoft, fg: colors.accent, label: 'Active' },
    past_due: { bg: '#FEF3C7', fg: '#92400E', label: 'Past due' },
    canceled: { bg: '#FEE2E2', fg: '#991B1B', label: 'Canceled' },
    incomplete: { bg: '#FEF3C7', fg: '#92400E', label: 'Incomplete' },
    none: { bg: colors.border, fg: colors.textMuted, label: 'None' },
  };
  const m = map[status] || map.none;
  return (
    <span style={{ padding: '3px 10px', borderRadius: 999, background: m.bg, color: m.fg, fontSize: 11, fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase' }}>
      {m.label}
    </span>
  );
}

// Small helper for callers that want the check-glyph outside the card itself.
export { Check as PlanCheck };
